import { type FC, type ReactNode } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

// MUI
import { Box, Typography } from '@mui/material';

// common
import SchemaForm from 'common/SchemaForm';
import type { MonthOption, WeekOption } from 'common/utils/schoolYearMonths';

import { DailyDishFilterModel, DailyDishStatusEnum } from 'modules/map/components/daily-dish/types';

interface FilterProps {
  filter: DailyDishFilterModel;
  setFilter: (filter: DailyDishFilterModel) => void;
  /** 12 tháng của năm học — thay cho 12 tháng của năm dương lịch. */
  monthOptions: MonthOption[];
  /** Tuần của đúng khung đang chọn (khung 4 hay 5 tuần là do MenuPlan, không tự tính). */
  weekOptions: WeekOption[];
  /** Không tải được khung thực đơn (MenuPlan lỗi) — khác hẳn "tháng này chưa chốt khung". */
  framesFailed?: boolean;
  /** Nút cạnh ô Tiến độ, cùng hàng Tháng / Tuần. */
  trailing?: ReactNode;
}

const Filter: FC<FilterProps> = ({
  filter,
  setFilter,
  monthOptions,
  weekOptions,
  framesFailed,
  trailing,
}) => {
  const intl = useIntl();

  // Ô Tuần rỗng thì phải nói vì sao: MenuPlan hỏng khác hẳn với tháng này chưa chốt khung.
  const emptyWeekText = intl.formatMessage({
    id: framesFailed ? 'map.weekLoadFailed' : 'map.noWeek',
  });

  return (
    <Box sx={{ p: 2, flexShrink: 0 }}>
      <SchemaForm
        formData={filter}
        onChange={setFilter}
        schema={{
          // Từ tablet trở lên mọi thứ phải nằm một hàng: ô chọn co lại chứ không đẩy nút xuống dòng.
          propsGridContainer: { sx: { flexWrap: { tablet: 'nowrap' } } },
          fields: ({ formProps: { isMobile } }) => {
            return {
              monthText: {
                mode: 'node',
                hidden: isMobile,
                render: () => {
                  return (
                    <Typography mt={1}>
                      <FormattedMessage id="map.month" />:
                    </Typography>
                  );
                },
                propsWrapper: { mobile: undefined, sx: { flexShrink: 0 } },
              },
              month: {
                mode: 'select',
                label: isMobile && <FormattedMessage id="map.month" />,
                options: monthOptions,
                rawOptions: true,
                disableClearBtn: true,
                noHelperText: true,
                propsWrapper: {
                  mobile: 4,
                  tablet: 3,
                  desktop: 2,
                  sx: { minWidth: 0 },
                },
              },
              weekText: {
                mode: 'node',
                hidden: isMobile,
                render: () => {
                  return (
                    <Box>
                      <Typography mt={1}>
                        <FormattedMessage id="map.week" />:
                      </Typography>
                      {weekOptions.length === 0 && (
                        <Typography variant="caption" color="text.secondary" component="div">
                          {emptyWeekText}
                        </Typography>
                      )}
                    </Box>
                  );
                },
                propsWrapper: { mobile: undefined, sx: { flexShrink: 0 } },
              },
              // Tuần phụ thuộc tháng nên đứng SAU tháng và khoá lại khi tháng chưa chốt khung.
              week: {
                mode: 'select',
                label: isMobile && <FormattedMessage id="map.week" />,
                options: weekOptions.map((w) => ({ id: w.id, name: w.id })),
                disabled: weekOptions.length === 0,
                rawOptions: true,
                disableClearBtn: true,
                noHelperText: true,
                propsWrapper: { mobile: 4, tablet: 2, sx: { minWidth: 0 } },
              },
              blank: {
                mode: 'node',
                hidden: isMobile,
                propsWrapper: { mobile: true },
              },
              statusText: {
                mode: 'node',
                hidden: isMobile,
                render: () => {
                  return (
                    <Typography mt={1}>
                      <FormattedMessage id="map.process" />
                    </Typography>
                  );
                },
                propsWrapper: { mobile: undefined, sx: { flexShrink: 0 } },
              },
              state: {
                mode: 'select',
                label: isMobile && <FormattedMessage id="map.process" />,
                options: [
                  {
                    id: DailyDishStatusEnum.propose,
                    name: intl.formatMessage({ id: 'map.propose' }),
                  },
                  {
                    id: DailyDishStatusEnum.confirm,
                    name: intl.formatMessage({ id: 'confirm' }),
                  },
                  {
                    id: DailyDishStatusEnum.complete,
                    name: intl.formatMessage({ id: 'map.completed' }),
                  },
                ],
                rawOptions: true,
                disableClearBtn: true,
                noHelperText: true,
                propsWrapper: { mobile: 4, tablet: 2.5, sx: { minWidth: 0 } },
              },
              trailing: {
                mode: 'node',
                hidden: !trailing,
                render: () => (
                  <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                    {trailing}
                  </Box>
                ),
                propsWrapper: {
                  mobile: 12,
                  tablet: 'auto',
                  sx: { display: 'flex', alignItems: 'center', flexShrink: 0 },
                },
              },
            };
          },
        }}
      />
    </Box>
  );
};

export default Filter;
